<template>
    <ContentLayout>
        <template v-if="contactError">
            <PageTitle :withBackButton="true" title="Klaida" />
            <Card>
                <template #content>
                    <p>Neturite prieigos prie šio kontakto arba kontaktas nerastas.</p>
                </template>
            </Card>
        </template>

        <template v-else>
            <div class="flex flex-row w-full">
                <PageTitle
                    :withBackButton="true"
                    :title="contactTitle"
                />

                <div class="flex flex-row gap-5 justify-end flex-1" v-if="capabilities.can_delete_contact">
                    <Button
                        label="Ištrinti kontaktą"
                        size="small"
                        class="self-start"
                        severity="danger"
                        @click="deleteContact"
                    />
                </div>
            </div>

            <div class="flex flex-col gap-4">
                <Card>
                    <template #content>
                        <div class="grid grid-cols-4 gap-3">
                            <FloatLabel
                                v-for="(field, index) in mainFields"
                                :key="index"
                                variant="on"
                            >
                                <template v-if="field.type == 'text'">
                                    <InputText
                                        id="over_label"
                                        v-model="contactForm[field.name]"
                                        class="w-full"
                                        :disabled="!capabilities.can_save_contact"
                                        size="small"
                                    />
                                    <label for="over_label" class="!font-normal">{{
                                        field.label
                                    }}</label>
                                </template>
                                <template v-if="field.type == 'select'">
                                    <Select
                                        v-model="contactForm[field.name]"
                                        :options="field.options"
                                        optionLabel="name"
                                        class="w-full"
                                        :disabled="!capabilities.can_save_contact"
                                        size="small"
                                    />
                                    <label for="over_label" class="!font-normal">{{
                                        field.label
                                    }}</label>
                                </template>
                                <template v-if="field.type == 'date'">
                                    <DatePicker
                                        v-model="contactForm[field.name]"
                                        class="w-full"
                                        dateFormat="yy-mm-dd"
                                        showIcon
                                        :disabled="!capabilities.can_save_contact"
                                        size="small"
                                    />
                                    <label for="over_label" class="!font-normal">{{
                                        field.label
                                    }}</label>
                                </template>
                            </FloatLabel>
                        </div>

                        <div class="mt-3" v-if="canViewDetailsPanel">
                            <Panel
                                ref="detailsPanel"
                                header="Kontakto anketa"
                                :collapsed="detailsIsCollapsed"
                                @update:collapsed="onDetailsCollapsedChange"
                            >
                                <div
                                    class="flex flex-row gap-4 mt-1 mb-4"
                                    v-if="
                                        capabilities.can_view_details_history ||
                                        canManageDetails ||
                                        capabilities.can_download_details
                                    "
                                >
                                    <div
                                        @click="viewDetailsHistory"
                                        class="text-blue-500 hover:text-blue-700 cursor-pointer"
                                        v-if="capabilities.can_view_details_history"
                                    >
                                        Peržiūrėti anketos istoriją
                                    </div>
                                    <div
                                        @click="downloadDetailsWordFile"
                                        class="text-blue-500 hover:text-blue-700 cursor-pointer"
                                        v-if="capabilities.can_download_details"
                                    >
                                        Atsisiųsti anketą .docx formatu
                                    </div>
                                </div>

                                <div class="grid grid-cols-4 gap-3 mt-1">
                                    <FloatLabel
                                        v-for="(field, index) in additionalFields"
                                        :key="index"
                                        variant="on"
                                    >
                                        <template v-if="field.type == 'text'">
                                            <Textarea
                                                id="over_label"
                                                v-model="contactDetailsForm[field.name]"
                                                class="w-full"
                                                autoResize
                                                rows="1"
                                                :disabled="!canManageDetails"
                                                size="small"
                                            />
                                            <label for="over_label" class="!font-normal">{{
                                                field.label
                                            }}</label>
                                        </template>
                                        <template v-if="field.type == 'select'">
                                            <Select
                                                v-model="contactDetailsForm[field.name]"
                                                :options="field.options"
                                                optionLabel="name"
                                                optionValue="code"
                                                class="w-full"
                                                :disabled="!canManageDetails"
                                                size="small"
                                            />
                                            <label for="over_label" class="!font-normal">{{
                                                field.label
                                            }}</label>
                                        </template>
                                    </FloatLabel>
                                </div>
                            </Panel>
                        </div>

                        <div
                            class="mt-3 flex flex-row items-center gap-4"
                            v-if="capabilities.can_save_contact"
                        >
                            <Button
                                label="Išsaugoti kontaktą"
                                size="small"
                                @click="saveContact"
                                :loading="contactSaving"
                            />

                            <div class="flex flex-row items-center gap-2">
                                <ToggleSwitch
                                    inputId="pinned_top"
                                    v-model="contactForm.pinned_top"
                                    :disabled="pinnedTopSaving"
                                    @update:modelValue="togglePinnedTop"
                                />
                                <label
                                    for="pinned_top"
                                    class="!font-normal flex items-center gap-2"
                                >
                                    <i class="pi pi-thumbtack text-orange-500"></i>
                                    <span>Prisegtas viršuje</span>
                                </label>
                            </div>

                            <Button
                                v-if="canAssignPartnerUsers"
                                label="Priskirti partnerį"
                                size="small"
                                outlined
                                class="ml-auto"
                                @click="assignPartnerPopoverToggle"
                            />
                        </div>
                    </template>
                </Card>

                <Card v-if="capabilities.can_view_special_attributes">
                    <template #content>
                        <div class="flex flex-row gap-2">
                            <Chip
                                v-if="!is('partner_collab')"
                                :class="specialAttributes.vip ? '!bg-lime-500 !text-white' : ''"
                                class="cursor-pointer"
                                @click="
                                    capabilities.can_manage_special_attributes &&
                                    toggleSpecialAttribute('vip')
                                "
                            >
                                <i v-if="specialAttributes.vip" class="pi pi-check-circle"></i>
                                <span class="">VIP</span>
                            </Chip>
                            <Chip
                                :class="
                                    specialAttributes.planned_trip ? '!bg-lime-500 !text-white' : ''
                                "
                                class="cursor-pointer"
                                @click="
                                    capabilities.can_manage_special_attributes &&
                                    toggleSpecialAttribute('planned_trip')
                                "
                            >
                                <i
                                    v-if="specialAttributes.planned_trip"
                                    class="pi pi-check-circle"
                                ></i>
                                <span class="">Suplanuota kelionė</span>
                            </Chip>
                            <Chip
                                :class="
                                    specialAttributes.offers_sent ? '!bg-lime-500 !text-white' : ''
                                "
                                class="cursor-pointer"
                                @click="
                                    capabilities.can_manage_special_attributes &&
                                    toggleSpecialAttribute('offers_sent')
                                "
                            >
                                <i
                                    v-if="specialAttributes.offers_sent"
                                    class="pi pi-check-circle"
                                ></i>
                                <span class="">Pasiūlymai išsiųsti</span>
                            </Chip>
                            <Chip
                                v-if="!is('partner_collab')"
                                :class="
                                    specialAttributes.local_meet ? '!bg-lime-500 !text-white' : ''
                                "
                                class="cursor-pointer"
                                @click="
                                    capabilities.can_manage_special_attributes &&
                                    toggleSpecialAttribute('local_meet')
                                "
                            >
                                <i
                                    v-if="specialAttributes.local_meet"
                                    class="pi pi-check-circle"
                                ></i>
                                <span class="">Susitikimas Lietuvoje</span>
                            </Chip>
                            <Chip
                                :class="
                                    specialAttributes.online_meet ? '!bg-lime-500 !text-white' : ''
                                "
                                class="cursor-pointer"
                                @click="
                                    capabilities.can_manage_special_attributes &&
                                    toggleSpecialAttribute('online_meet')
                                "
                            >
                                <i
                                    v-if="specialAttributes.online_meet"
                                    class="pi pi-check-circle"
                                ></i>
                                <span class="">Online peržiūra / susitikimas</span>
                            </Chip>
                            <Chip
                                :class="
                                    specialAttributes.updated_offers
                                        ? '!bg-lime-500 !text-white'
                                        : ''
                                "
                                class="cursor-pointer"
                                @click="
                                    capabilities.can_manage_special_attributes &&
                                    toggleSpecialAttribute('updated_offers')
                                "
                            >
                                <i
                                    v-if="specialAttributes.updated_offers"
                                    class="pi pi-check-circle"
                                ></i>
                                <span class="">Atnaujinti pasiūlymai</span>
                            </Chip>
                            <Chip
                                v-if="!is('partner_collab')"
                                :class="
                                    specialAttributes.commercial ? '!bg-lime-500 !text-white' : ''
                                "
                                class="cursor-pointer"
                                @click="
                                    capabilities.can_manage_special_attributes &&
                                    toggleSpecialAttribute('commercial')
                                "
                            >
                                <i
                                    v-if="specialAttributes.commercial"
                                    class="pi pi-check-circle"
                                ></i>
                                <span class="">Komercija</span>
                            </Chip>
                        </div>
                    </template>
                </Card>

                <Card v-if="!is('partner_readonly')">
                    <template #content>
                        <div class="flex flex-row gap-2 mb-4" v-if="capabilities.can_delegate_contact || capabilities.can_manage_assignments || capabilities.can_view_delegation_history">
                            <Button
                                label="Priskirti"
                                outlined
                                @click="assignPopoverToggle"
                                v-if="capabilities.can_manage_assignments"
                            ></Button>
                            <Button
                                label="Deleguoti"
                                outlined
                                @click="delegatePopoverToggle"
                                v-if="capabilities.can_delegate_contact && hasAvailableDelegationUsers"
                            ></Button>
                            <Button
                                label="Delegavimo istorija"
                                outlined
                                @click="viewDelegationHistory"
                                v-if="capabilities.can_view_delegation_history"
                            ></Button>
                        </div>

                        <div class="flex flex-col gap-2">
                            <strong>Priskirti vartotojai:</strong>
                            <div>
                                <template v-if="contact.users">
                                    <div class="flex flex-col gap-2">
                                        <div
                                            class="flex flex-row gap-3 items-center"
                                            v-for="user in contact.users"
                                            :key="user.id"
                                        >
                                            <PVButton
                                                v-if="capabilities.can_manage_assignments"
                                                class="!w-8 !h-8"
                                                icon="pi pi-trash"
                                                @click="deleteAssignedUser(user.id)"
                                                severity="danger"
                                                size="small"
                                            ></PVButton>

                                            {{ user.first_name }}
                                        </div>
                                    </div>
                                </template>
                                <template v-else> Priskirtų vartotojų nėra </template>
                            </div>
                        </div>
                    </template>
                </Card>

                <ContactFiles
                    v-if="isContactLoaded && capabilities.can_view_files"
                    :contact="contact"
                ></ContactFiles>
                <ContactComments
                    v-if="isContactLoaded"
                    :contact="contact"
                    @draft-changed="onCommentsDraftChanged"
                ></ContactComments>
                <ContactReminders
                    v-if="isContactLoaded && capabilities.can_view_reminders"
                    :contact="contact"
                    @draft-changed="onRemindersDraftChanged"
                ></ContactReminders>
                <PrivateNotes
                    v-if="isContactLoaded && capabilities.can_view_private_notes"
                    :contact="contact"
                    @draft-changed="onPrivateNotesDraftChanged"
                ></PrivateNotes>
                <ContactActions v-if="isContactLoaded" :contact="contact"></ContactActions>
            </div>
        </template>
    </ContentLayout>

    <Dialog
        v-model:visible="isDetailsHistoryModalVisible"
        modal
        header="History"
        :style="{ width: '800px' }"
    >
        <table class="table-auto w-full border-collapse border border-gray-300">
            <thead>
                <tr class="bg-gray-100">
                    <th class="border p-2">Vartotojas</th>
                    <th class="border p-2">Data</th>
                    <th class="border p-2">Pakeitimai</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="item in detailsHistory" :key="item.id">
                    <td class="border p-2">{{ item.author }}</td>
                    <td class="border p-2">{{ item.created_at }}</td>
                    <td class="border p-2">
                        <table class="w-full">
                            <thead>
                                <tr class="bg-gray-50 text-left text-sm text-gray-700">
                                    <th class="p-1 w-[400px]">Laukelis</th>
                                    <th class="p-1 w-[200px]">Sena reikšmė</th>
                                    <th class="p-1 w-[200px]">Nauja reikšmė</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr class="text-sm" v-for="st in item.structured_data">
                                    <td class="p-1 font-medium">
                                        {{ detailsHistoryFieldsNames[st.field] }}
                                    </td>
                                    <td class="p-1 text-red-600">{{ st.old }}</td>
                                    <td class="p-1 text-green-600">{{ st.new }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </td>
                </tr>
            </tbody>
        </table>
    </Dialog>

    <Dialog
        v-model:visible="isDelegationHistoryModalVisible"
        modal
        header="Delegavimo istorija"
        :style="{ width: '900px' }"
        :pt="{
            content: {
                class: '!p-0 !overflow-hidden'
            }
        }"
    >
        <div v-if="delegationHistoryLoading" class="p-4 text-sm text-gray-500">Kraunama...</div>

        <div v-else-if="delegationHistory.length === 0" class="p-4 text-sm text-gray-500">
            Delegavimo istorijos įrašų nėra.
        </div>

        <table v-else class="w-full border-collapse">
            <thead>
                <tr class="bg-slate-50 border-b border-slate-200">
                    <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                        Delegavo
                    </th>
                    <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                        Kam delegavo
                    </th>
                    <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                        Kas deleguota
                    </th>
                    <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                        Žinutė
                    </th>
                    <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                        Data
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="item in delegationHistory"
                    :key="item.id"
                    class="border-b border-slate-100 last:border-b-0"
                >
                    <td class="px-4 py-3 text-sm text-slate-700">{{ item.delegated_by }}</td>
                    <td class="px-4 py-3 text-sm text-slate-700">{{ item.delegated_to }}</td>
                    <td class="px-4 py-3 text-sm text-slate-700">{{ item.delegated_item }}</td>
                    <td class="px-4 py-3 text-sm text-slate-700 whitespace-pre-wrap">
                        {{ item.message || '-' }}
                    </td>
                    <td class="px-4 py-3 text-sm text-slate-700">{{ item.created_at }}</td>
                </tr>
            </tbody>
        </table>
    </Dialog>

    <Popover ref="assignPopover" v-if="capabilities.can_manage_assignments">
        <div class="flex flex-col gap-4 w-[15rem]">
            <FloatLabel variant="on">
                <Select
                    v-model="assignSelectedUser"
                    :options="assignAvailableUsers"
                    optionLabel="name"
                    class="w-full"
                    size="small"
                />
                <label for="over_label">Vartotojas</label>
            </FloatLabel>

            <Button label="Priskirti" size="small" @click="assignUser" class="self-start" />
        </div>
    </Popover>

    <Popover ref="assignPartnerPopover" v-if="canAssignPartnerUsers">
        <div class="flex flex-col gap-4 w-[15rem]">
            <FloatLabel variant="on">
                <Select
                    v-model="assignPartnerSelectedUser"
                    :options="assignPartnerAvailableUsers"
                    optionLabel="name"
                    class="w-full"
                    size="small"
                />
                <label for="over_label">Partneris</label>
            </FloatLabel>

            <Button
                label="Priskirti partnerį"
                size="small"
                @click="assignPartnerUser"
                class="self-start"
            />
        </div>
    </Popover>

    <Popover ref="delegatePopover" v-if="capabilities.can_delegate_contact">
        <div class="flex flex-col gap-4 w-[15rem]">
            <FloatLabel variant="on">
                <Select
                    v-model="delegateSelectedUser"
                    :options="delegateAvailableUsers"
                    optionLabel="name"
                    class="w-full"
                    size="small"
                />
                <label for="over_label">Vartotojas</label>
            </FloatLabel>

            <FloatLabel variant="on">
                <Textarea v-model="delegationMessage" class="w-full" size="small" autoResize />
                <label for="over_label" class="!font-normal">Žinutė (neprivaloma)</label>
            </FloatLabel>

            <Button label="Deleguoti" size="small" @click="delegate" class="self-start" />
        </div>
    </Popover>
</template>

<script setup lang="ts">
// System imports
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useRouter, onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router'
import { apiRequest } from '@/services/axios.ts'
import { is } from '@/extensions/permissions.ts'
import { Auth } from '@/stores/auth'

// Local components imports
import ContentLayout from '@/components/layouts/Content.vue'
import Button from '@/components/buttons/Button.vue'
import PageTitle from '@/components/navigation/PageTitle.vue'
import ContactFiles from '@/pages/contacts/components/Files.vue'
import ContactComments from '@/pages/contacts/components/Comments.vue'
import ContactReminders from '@/pages/contacts/components/Reminders.vue'
import ContactActions from '@/pages/contacts/components/ActionsHistory.vue'
import PrivateNotes from '@/pages/contacts/components/PrivateNotes.vue'

// PrimeVue components imports
import { useConfirm } from 'primevue/useconfirm'
import Textarea from 'primevue/textarea'
import FloatLabel from 'primevue/floatlabel'
import Card from 'primevue/card'
import PVButton from 'primevue/button'
import Chip from 'primevue/chip'
import Select from 'primevue/select'
import InputText from 'primevue/inputtext'
import DatePicker from 'primevue/datepicker'
import Panel from 'primevue/panel'
import Dialog from 'primevue/dialog'
import Popover from 'primevue/popover'
import ToggleSwitch from 'primevue/toggleswitch'

// Refs and variables
const confirm = useConfirm()
const router = useRouter()
const auth = Auth()
const contact = ref<any>([])
const contactForm = ref<any>({
    firstname: null,
    lastname: null,
    budget: null,
    email: null,
    phone: null,
    region: null,
    city: null,
    author: null,
    stage: null,
    type: null,
    source: null,
    contact_date: null,
    pinned_top: false
})
const contactDetailsForm = ref<any>({
    purchase_location: null,
    purpose: null,
    moving_time: null,
    market_type: null,
    acquisition_term: null,
    payment_method: null,
    housing_type: null,
    bedrooms: null,
    bathrooms: null,
    floor: null,
    terrace: null,
    parking: null,
    source: null,
    pool: null,
    territory: null,
    window_view: null,
    sea_distance: null,
    airport_distance: null,
    plan_b: null,
    spain_experience: null,
    activities: null,
    important_aspects: null
})
const isContactLoaded = ref<boolean>(false)
const contactError = ref<boolean>(false)
const contactSaving = ref()
const pinnedTopSaving = ref(false)
const mainFields = ref()
const additionalFields = ref()

const detailsIsCollapsed = ref(true)
const detailsPanel = ref<any>(null)

const detailsHistory = ref<any>([])
const detailsHistoryFieldsNames = ref<any>()
const isDetailsHistoryModalVisible = ref(false)
const isDelegationHistoryModalVisible = ref(false)
const delegationHistoryLoading = ref(false)
const delegationHistory = ref<any>([])
const contactFormSnapshot = ref<string>('')
const contactDetailsFormSnapshot = ref<string>('')
const commentsDraftDirty = ref(false)
const privateNotesDraftDirty = ref(false)
const remindersDraftDirty = ref(false)
const canManageDetails = ref(false)
const canViewDetailsPanel = ref(false)
const capabilities = ref<any>({
    can_delete_contact: false,
    can_save_contact: false,
    can_view_special_attributes: false,
    can_manage_special_attributes: false,
    can_manage_assignments: false,
    can_delegate_contact: false,
    can_view_delegation_history: false,
    can_view_details_history: false,
    can_download_details: false,
    can_view_files: false,
    can_view_reminders: false,
    can_view_private_notes: false
})

const assignPopover = ref()
const assignSelectedUser = ref()
const assignAvailableUsers = ref()
const assignPartnerPopover = ref()
const assignPartnerSelectedUser = ref()
const assignPartnerAvailableUsers = ref()

const delegatePopover = ref()
const delegationMessage = ref()
const delegateSelectedUser = ref()
const delegateAvailableUsers = ref()

const specialAttributes = ref<any>({
    vip: false,
    planned_trip: false,
    offers_sent: false,
    local_meet: false,
    online_meet: false,
    updated_offers: false,
    commercial: false
})

// Computed properties
const contactTitle = computed(() => {
    const name = fullName(contact.value?.first_name, contact.value?.last_name)

    return name ? `Kontaktas ${name}` : 'Kontaktas'
})

const hasAvailableDelegationUsers = computed(() => {
    if (!delegateAvailableUsers.value || delegateAvailableUsers.value.length === 0) {
        return false
    }

    if (!contact.value.users || contact.value.users.length === 0) {
        return true
    }

    // Get IDs of already assigned users
    const assignedUserIds = contact.value.users.map((user: any) => user.id)

    // Check if there are any users in delegate list that are not already assigned
    return delegateAvailableUsers.value.some((user: any) => !assignedUserIds.includes(user.code))
})
const canAssignPartnerUsers = computed(() => is('admin|manager|manager_limited'))

const hasUnsavedContactEdits = computed(() => {
    if (!isContactLoaded.value || !contactFormSnapshot.value || !contactDetailsFormSnapshot.value) {
        return false
    }

    return (
        serializeContactFormState() !== contactFormSnapshot.value ||
        serializeContactDetailsState() !== contactDetailsFormSnapshot.value
    )
})

const hasUnsavedChanges = computed(() => {
    return (
        hasUnsavedContactEdits.value ||
        commentsDraftDirty.value ||
        privateNotesDraftDirty.value ||
        remindersDraftDirty.value
    )
})

const unsavedChangesMessage = 'Yra neišsaugotų pakeitimų, ar tikrai norite išeiti?'

const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (!hasUnsavedChanges.value) {
        return
    }

    event.preventDefault()
    event.returnValue = unsavedChangesMessage
}

// Hooks
onMounted(async () => {
    // Load contact data first
    await getContact()

    // Then load other data in parallel
    const promises = []

    if (capabilities.value.can_view_details_history) {
        promises.push(loadDetailsHistory())
    }

    if (capabilities.value.can_manage_assignments) {
        promises.push(loadAvailableToAssignUsers())
    }

    if (canAssignPartnerUsers.value) {
        promises.push(loadAvailableToAssignPartnerUsers())
    }

    if (capabilities.value.can_delegate_contact) {
        promises.push(loadAvailableToDelegateUsers())
    }

    await Promise.all(promises)

    // DOM manipulation after data is loaded
    if (canViewDetailsPanel.value) {
        // Wait for DOM render
        const header = detailsPanel.value?.$el?.querySelector('.p-panel-header')
        if (header) {
            header.style.cursor = 'pointer'
            header.addEventListener('click', () => {
                detailsIsCollapsed.value = !detailsIsCollapsed.value
            })
        }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
})

onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload)
})

onBeforeRouteLeave(() => {
    if (!hasUnsavedChanges.value) {
        return true
    }

    return window.confirm(unsavedChangesMessage)
})

onBeforeRouteUpdate(() => {
    if (!hasUnsavedChanges.value) {
        return true
    }

    return window.confirm(unsavedChangesMessage)
})

// Methods
function fullName(firstName: unknown, lastName: unknown): string {
    return [namePart(firstName), namePart(lastName)].filter(Boolean).join(' ')
}

function namePart(value: unknown): string {
    if (value === null || value === undefined) {
        return ''
    }

    const normalizedValue = String(value).trim()

    return ['null', 'undefined'].includes(normalizedValue.toLowerCase()) ? '' : normalizedValue
}

async function getContact() {
    let response
    try {
        response = await apiRequest('get', '/contacts/' + router.currentRoute.value.params.id)
    } catch (error) {
        contactError.value = true
        return
    }

    mainFields.value = response.data.data.main_fields

    additionalFields.value = response.data.data.additional_fields ?? []
    canViewDetailsPanel.value = Boolean(
        response.data.data.can_view_details ?? additionalFields.value.length > 0
    )
    canManageDetails.value = Boolean(
        response.data.data.can_manage_details ?? canViewDetailsPanel.value
    )
    capabilities.value = {
        ...capabilities.value,
        ...(response.data.data.capabilities ?? {})
    }

    contact.value = response.data.data.contact

    contactForm.value.firstname = response.data.data.contact.first_name
    contactForm.value.lastname = response.data.data.contact.last_name
    contactForm.value.budget = response.data.data.contact.budget
    contactForm.value.email = response.data.data.contact.email
    contactForm.value.phone = response.data.data.contact.phone
    contactForm.value.region = response.data.data.contact.region
    contactForm.value.city = response.data.data.contact.location
    contactForm.value.author =
        response.data.data.contact.creator?.first_name +
        ' ' +
        response.data.data.contact.creator?.last_name
    contactForm.value.stage = {
        code: response.data.data.contact.stage,
        name: response.data.data.contact.stage_name
    }
    contactForm.value.type = {
        code: response.data.data.contact.type,
        name: response.data.data.contact.type_name
    }
    contactForm.value.source = response.data.data.contact.contact_source
    contactForm.value.pinned_top = Boolean(response.data.data.contact.pinned_top)
    // Parse date string to avoid timezone issues
    if (response.data.data.contact.contact_date) {
        const [year, month, day] = response.data.data.contact.contact_date.split('-')
        contactForm.value.contact_date = new Date(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day)
        )
    } else {
        contactForm.value.contact_date = null
    }

    if (canViewDetailsPanel.value) {
        contactDetailsForm.value.purchase_location =
            response.data.data.contact?.form?.purchase_location ?? null
        contactDetailsForm.value.purpose = response.data.data.contact?.form?.purpose ?? null
        contactDetailsForm.value.moving_time = response.data.data.contact?.form?.moving_time ?? null
        contactDetailsForm.value.market_type = response.data.data.contact?.form?.market_type ?? null
        contactDetailsForm.value.acquisition_term =
            response.data.data.contact?.form?.acquisition_term ?? null
        contactDetailsForm.value.payment_method =
            response.data.data.contact?.form?.payment_method ?? null
        contactDetailsForm.value.housing_type =
            response.data.data.contact?.form?.housing_type ?? null
        contactDetailsForm.value.bedrooms = response.data.data.contact?.form?.bedrooms ?? null
        contactDetailsForm.value.bathrooms = response.data.data.contact?.form?.bathrooms ?? null
        contactDetailsForm.value.floor = response.data.data.contact?.form?.floor ?? null
        contactDetailsForm.value.terrace = response.data.data.contact?.form?.terrace ?? null
        contactDetailsForm.value.parking = response.data.data.contact?.form?.parking ?? null
        contactDetailsForm.value.source = response.data.data.contact?.form?.source ?? null
        contactDetailsForm.value.pool = response.data.data.contact?.form?.pool ?? null
        contactDetailsForm.value.territory = response.data.data.contact?.form?.territory ?? null
        contactDetailsForm.value.window_view = response.data.data.contact?.form?.window_view ?? null
        contactDetailsForm.value.sea_distance =
            response.data.data.contact?.form?.sea_distance ?? null
        contactDetailsForm.value.airport_distance =
            response.data.data.contact?.form?.airport_distance ?? null
        contactDetailsForm.value.plan_b = response.data.data.contact?.form?.plan_b ?? null
        contactDetailsForm.value.spain_experience =
            response.data.data.contact?.form?.spain_experience ?? null
        contactDetailsForm.value.activities = response.data.data.contact?.form?.activities ?? null
        contactDetailsForm.value.important_aspects =
            response.data.data.contact?.form?.important_aspects ?? null
    }

    if (capabilities.value.can_view_special_attributes) {
        specialAttributes.value.vip = response.data.data.contact?.vip ?? false
        specialAttributes.value.planned_trip = response.data.data.contact?.planned_trip ?? false
        specialAttributes.value.offers_sent = response.data.data.contact?.offers_sent ?? false
        specialAttributes.value.local_meet = response.data.data.contact?.local_meet ?? false
        specialAttributes.value.online_meet = response.data.data.contact?.online_meet ?? false
        specialAttributes.value.updated_offers = response.data.data.contact?.updated_offers ?? false
        specialAttributes.value.commercial = response.data.data.contact?.commercial ?? false
    }

    captureContactFormSnapshot()
    isContactLoaded.value = true
}

async function saveContact() {
    // Format the contact_date if it's a Date object
    let formattedDate = null
    if (contactForm.value.contact_date) {
        const date = new Date(contactForm.value.contact_date)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        formattedDate = `${year}-${month}-${day}`
    }

    const formData = {
        ...contactForm.value,
        contact_date: formattedDate
    }

    await apiRequest('put', '/contacts/' + router.currentRoute.value.params.id, {
        contact: formData,
        contact_details: contactDetailsForm.value
    })

    if (capabilities.value.can_view_details_history) {
        await loadDetailsHistory()
    }

    captureContactFormSnapshot()
}

async function togglePinnedTop(newValue: boolean) {
    const previousValue = !newValue
    pinnedTopSaving.value = true

    try {
        await apiRequest(
            'patch',
            '/contacts/' + router.currentRoute.value.params.id + '/toggle-pinned-top',
            {
                pinned_top: newValue
            }
        )

        contactForm.value.pinned_top = newValue
        if (contact.value) {
            contact.value.pinned_top = newValue
        }
        captureContactFormSnapshot()
    } catch {
        contactForm.value.pinned_top = previousValue
        if (contact.value) {
            contact.value.pinned_top = previousValue
        }
    } finally {
        pinnedTopSaving.value = false
    }
}

function serializeContactFormState() {
    const stage = contactForm.value?.stage
    const type = contactForm.value?.type

    return JSON.stringify({
        firstname: contactForm.value?.firstname ?? null,
        lastname: contactForm.value?.lastname ?? null,
        budget: contactForm.value?.budget ?? null,
        email: contactForm.value?.email ?? null,
        phone: contactForm.value?.phone ?? null,
        region: contactForm.value?.region ?? null,
        city: contactForm.value?.city ?? null,
        stage: typeof stage === 'object' && stage !== null ? stage.code ?? null : stage ?? null,
        type: typeof type === 'object' && type !== null ? type.code ?? null : type ?? null,
        source: contactForm.value?.source ?? null,
        contact_date: normalizeContactDate(contactForm.value?.contact_date),
        pinned_top: Boolean(contactForm.value?.pinned_top)
    })
}

function serializeContactDetailsState() {
    const details = contactDetailsForm.value ?? {}
    const normalized: Record<string, any> = {}

    Object.keys(details).forEach((key) => {
        normalized[key] = details[key] ?? null
    })

    return JSON.stringify(normalized)
}

function normalizeContactDate(dateValue: any) {
    if (!dateValue) {
        return null
    }

    const date = dateValue instanceof Date ? dateValue : new Date(dateValue)
    if (Number.isNaN(date.getTime())) {
        return null
    }

    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
}

function captureContactFormSnapshot() {
    contactFormSnapshot.value = serializeContactFormState()
    contactDetailsFormSnapshot.value = serializeContactDetailsState()
}

function onCommentsDraftChanged(isDirty: boolean) {
    commentsDraftDirty.value = isDirty
}

function onPrivateNotesDraftChanged(isDirty: boolean) {
    privateNotesDraftDirty.value = isDirty
}

function onRemindersDraftChanged(isDirty: boolean) {
    remindersDraftDirty.value = isDirty
}

async function loadDetailsHistory() {
    const response = await apiRequest(
        'get',
        '/contacts/' + router.currentRoute.value.params.id + '/details-history'
    )

    detailsHistoryFieldsNames.value = response.data.data.fields_names
    detailsHistory.value = response.data.data.history
}

function viewDetailsHistory() {
    isDetailsHistoryModalVisible.value = true
}

async function loadDelegationHistory() {
    delegationHistoryLoading.value = true

    try {
        const response = await apiRequest(
            'get',
            '/contacts/' + router.currentRoute.value.params.id + '/delegation-history'
        )

        delegationHistory.value = response.data.data.history ?? []
    } catch {
        delegationHistory.value = []
    } finally {
        delegationHistoryLoading.value = false
    }
}

async function viewDelegationHistory() {
    isDelegationHistoryModalVisible.value = true
    await loadDelegationHistory()
}

async function downloadDetailsWordFile() {
    const response = await apiRequest(
        'get',
        '/contacts/' + router.currentRoute.value.params.id + '/download-details',
        {},
        {
            responseType: 'blob'
        }
    )

    const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'Kliento_anketa.docx')
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
}

async function assignUser() {
    await apiRequest('post', '/contacts/' + router.currentRoute.value.params.id + '/assign-user', {
        user: assignSelectedUser.value ? assignSelectedUser.value.code : null
    })

    await getContact()

    assignSelectedUser.value = null
    assignPopover.value.toggle()
}

async function assignPartnerUser() {
    await apiRequest('post', '/contacts/' + router.currentRoute.value.params.id + '/assign-user', {
        user: assignPartnerSelectedUser.value ? assignPartnerSelectedUser.value.code : null
    })

    await getContact()

    assignPartnerSelectedUser.value = null
    assignPartnerPopover.value.toggle()
}

async function deleteAssignedUser(userId: number) {
    confirm.require({
        message: 'Ar tikrai norite ištrinti priskirtą vartotoją?',
        header: 'Patvirtinimas',
        icon: 'pi pi-exclamation-triangle',
        rejectProps: {
            label: 'Ne',
            severity: 'secondary',
            outlined: true
        },
        acceptProps: {
            label: 'Taip'
        },
        accept: async () => {
            await apiRequest(
                'post',
                '/contacts/' + router.currentRoute.value.params.id + '/remove-assigned-user',
                {
                    user: userId
                }
            )

            await getContact()
        }
    })
}

async function delegate() {
    await apiRequest('post', '/contacts/' + router.currentRoute.value.params.id + '/delegate', {
        message: delegationMessage.value,
        user_id: delegateSelectedUser.value ? delegateSelectedUser.value.code : null
    })

    await getContact()

    delegationMessage.value = null
    delegateSelectedUser.value = null
    delegatePopover.value.toggle()
}

async function loadAvailableToAssignUsers() {
    const response = await apiRequest(
        'get',
        '/contacts/' + router.currentRoute.value.params.id + '/available-to-assign-users'
    )

    assignAvailableUsers.value = response.data.data.users
}

async function loadAvailableToAssignPartnerUsers() {
    const response = await apiRequest(
        'get',
        '/contacts/' + router.currentRoute.value.params.id + '/available-to-assign-partner-users'
    )

    assignPartnerAvailableUsers.value = response.data.data.users
}

async function loadAvailableToDelegateUsers() {
    const response = await apiRequest(
        'get',
        '/contacts/' + router.currentRoute.value.params.id + '/available-to-delegate-users'
    )

    delegateAvailableUsers.value = response.data.data.users

    // Auto-select the first (and only) user
    if (delegateAvailableUsers.value && delegateAvailableUsers.value.length > 0) {
        delegateSelectedUser.value = delegateAvailableUsers.value[0]
    }
}

async function deleteContact() {
    confirm.require({
        message: 'Ar tikrai norite ištrinti šį kontaktą?',
        header: 'Patvirtinimas',
        icon: 'pi pi-exclamation-triangle',
        rejectProps: {
            label: 'Ne',
            severity: 'secondary',
            outlined: true
        },
        acceptProps: {
            label: 'Taip'
        },
        accept: async () => {
            await apiRequest('delete', '/contacts/' + router.currentRoute.value.params.id)

            router.push({ name: 'contacts' })
        }
    })
}

async function toggleSpecialAttribute(key: string) {
    await apiRequest(
        'patch',
        '/contacts/' + router.currentRoute.value.params.id + '/toggle-special-attribute',
        {
            key: key
        }
    )

    if (specialAttributes.value[key]) {
        specialAttributes.value[key] = false
    } else {
        specialAttributes.value[key] = true
    }
}

const onDetailsCollapsedChange = (val: any) => {
    detailsIsCollapsed.value = val
}

const assignPopoverToggle = (event: any) => {
    assignPopover.value.toggle(event)
}

const assignPartnerPopoverToggle = (event: any) => {
    assignPartnerPopover.value.toggle(event)
}

const delegatePopoverToggle = (event: any) => {
    delegatePopover.value.toggle(event)
}
</script>
